import React, { useState } from 'react';
import { resolveAssetUrl } from '../utils/resolveUrl';

export default function ProductLoanWidget({ loanInfo, onApplyClick }) {
  const [selectedMonths, setSelectedMonths] = useState(null);

  if (!loanInfo || !loanInfo.isEligible || !loanInfo.productView?.showBox) {
    return null;
  }

  const { productView = {}, terms = [], partnerName, partnerLogo } = loanInfo;
  const initialMonths = productView.defaultSelectedTerm?.months || (terms[0]?.months || 12);
  const currentMonths = selectedMonths !== null ? selectedMonths : initialMonths;

  const activeTerm = terms.find(t => t.months === currentMonths) || terms[0];
  if (!activeTerm) return null;

  const partnerLogoUrl = partnerLogo ? resolveAssetUrl(partnerLogo) : null;

  return (
    <div
      className="product-loan-widget"
      style={{
        marginTop: 14,
        padding: '14px 16px',
        background: 'linear-gradient(135deg, #f0fdfa 0%, #f8fafc 100%)',
        border: '1.5px solid #007da5',
        borderRadius: 12,
        boxShadow: '0 3px 12px rgba(0, 125, 165, 0.08)',
        position: 'relative'
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {partnerLogoUrl ? (
            <img
              src={partnerLogoUrl}
              alt={partnerName || 'Partner'}
              style={{ height: 22, maxWidth: 65, objectFit: 'contain' }}
            />
          ) : (
            <div style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: '#007da5',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14
            }}>
              💳
            </div>
          )}
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
              {productView.boxTitle || 'Mundësi Blerje me Këste'}
            </div>
            {productView.boxSubtitle && (
              <div style={{ fontSize: 11.5, color: '#0284c7', marginTop: 2, fontWeight: 500 }}>
                {productView.boxSubtitle}
              </div>
            )}
          </div>
        </div>

        {partnerName && (
          <span style={{
            fontSize: 10.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            color: '#0369a1',
            background: '#e0f2fe',
            padding: '3px 8px',
            borderRadius: 20
          }}>
            {partnerName}
          </span>
        )}
      </div>

      {/* Terms Selector Tabs (e.g. 12 Muaj vs 36 Muaj) */}
      {productView.showTermsSelector && terms.length > 1 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${terms.length}, 1fr)`,
          gap: 6,
          marginBottom: 12
        }}>
          {terms.map(t => {
            const isSelected = t.months === activeTerm.months;
            return (
              <button
                key={t.months}
                type="button"
                onClick={() => setSelectedMonths(t.months)}
                style={{
                  padding: '8px 6px',
                  borderRadius: 8,
                  border: isSelected ? '2px solid #007da5' : '1px solid #cbd5e1',
                  background: isSelected ? '#ffffff' : '#f8fafc',
                  color: isSelected ? '#007da5' : '#475569',
                  fontWeight: isSelected ? 700 : 500,
                  fontSize: 12.5,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  boxShadow: isSelected ? '0 2px 6px rgba(0, 125, 165, 0.15)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                <span>{t.label || `${t.months} Muaj`}</span>
                <span style={{
                  fontSize: 11,
                  fontWeight: isSelected ? 700 : 600,
                  color: isSelected ? '#0f172a' : '#64748b'
                }}>
                  {t.monthlyFormatted}/m
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Highlight Box for Active Term */}
      <div style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 8
      }}>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>
            Pagesa Mujore ({activeTerm.months} Muaj)
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#007da5', lineHeight: 1.2, marginTop: 2 }}>
            {activeTerm.monthlyFormatted} <span style={{ fontSize: 13, fontWeight: 500, color: '#64748b' }}>/ muaj</span>
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
            {activeTerm.interestRate === 0 ? 'Interes 0%' : `Interes: ${activeTerm.interestRate}%`}
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', marginTop: 2 }}>
            Totali: {activeTerm.totalFormatted}
          </div>
        </div>
      </div>

      {/* Note / Terms Information */}
      {productView.note && (
        <div style={{
          marginTop: 10,
          fontSize: 11.5,
          color: '#475569',
          display: 'flex',
          alignItems: 'center',
          gap: 6
        }}>
          <span style={{ color: '#10b981', fontSize: 13 }}>✓</span>
          <span>{productView.note}</span>
        </div>
      )}
    </div>
  );
}
